<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { getAvailabilityOptions } from "@/helpers/filterOptions";

import type { supporttedEventNames } from "@/dsComponents/Dropdown/types";
import type { LabelValue } from '@/types/common';

import { DropdownItem } from "@/dsComponents/Dropdown";
import ControlledDropdown from "@/dsComponents/Dropdown/ControlledDropdown.vue";
import ComboTagInput from "@/dsComponents/inputs/ComboTagInput.vue";
import Separator from "@/dsComponents/Separator.vue";

interface AvailabilitiesDropdownProps {
    modelValue: LabelValue<string>[];
    placeholder?: string;
    icon?: string;
    showIcon?: boolean;
    fitContent?: boolean;
    fullWidth?: boolean;
}

const props = withDefaults(defineProps<AvailabilitiesDropdownProps>(), {
    icon: "cal-dot",
    showIcon: true,
    fitContent: true,
    fullWidth: false,
});

const emit = defineEmits<{
    "update:modelValue": [value: LabelValue<string>[]];
}>();

const { t } = useI18n();
const isOpen = ref(false);
const availabilityOptions = computed(() => getAvailabilityOptions());

const handleControlledDropdownChange = (event: supporttedEventNames) => {
    if (event === "clickaway") isOpen.value = false;
};

const handleToggle = () => {
    isOpen.value = !isOpen.value;
};

const handleSelect = (option: LabelValue<string>) => {
    const isAlreadySelected = props.modelValue.some((v) => v.value === option.value);

    if (isAlreadySelected) {
        handleDelete(option.value);
        return;
    }

    emit("update:modelValue", [...props.modelValue, option]);
};

const handleDelete = (value: string) => {
    emit(
        "update:modelValue",
        props.modelValue.filter((v) => v.value !== value)
    );
};
</script>
<template>
    <ControlledDropdown :open="isOpen" :fit-content="fitContent" @change="handleControlledDropdownChange">
        <template #trigger-zone>
            <ComboTagInput
                :items="modelValue"
                :icon="showIcon ? icon : undefined"
                :placeholder="placeholder ?? t('global.dropdowns.availabilities.placeholder')"
                :full-width="fullWidth"
                @click="handleToggle"
                @delete="handleDelete($event)"
            />
        </template>
        <template #dropdown-content>
            <div class="availabilities-dropdown__title typo-xs-medium">
                {{ t("global.dropdowns.availabilities.title") }}
            </div>
            <Separator />
            <DropdownItem
                v-for="option in availabilityOptions"
                :key="option.value"
                :label="option.label"
                :checked="modelValue.some((v) => v.value === option.value)"
                :checkbox="true"
                @select="handleSelect(option)"
            />
        </template>
    </ControlledDropdown>
</template>

<style lang="scss" scoped>
.availabilities-dropdown {
    &__title {
        display: flex;
        align-items: center;
        height: 32px;
        padding: 0 10px;
        color: var(--text-soft-400, #9297aa);
    }
}
</style>
